import { GpmPay } from '../core/client.js';
import { GpmPayConfigError } from '../core/errors.js';
import { SANDBOX_BASE_URL } from '../core/config.js';

export interface GlobalFlags {
  token?: string | undefined;
  baseUrl?: string | undefined;
  sandbox?: boolean | undefined;
  json?: boolean | undefined;
}

export const NO_TOKEN_MESSAGE = [
  'No API token found.',
  '',
  '  Set one of:',
  '    export GPMPAY_API_TOKEN=gpm_xxxxxxxx_yyyyyyyyyyyyyyyyyyyyyyyy',
  '    gpmpay ping --token gpm_...',
  '',
  '  Create a token at https://app.gpmpay.com/api-tokens',
  '  (Node 20.6+: you can also load a .env file with `node --env-file=.env`.)',
].join('\n');

/**
 * Build a client from flags and the environment.
 *
 * @throws {GpmPayConfigError} with CLI-shaped guidance when no token is available.
 */
export function clientFromFlags(flags: GlobalFlags): GpmPay {
  const token = flags.token ?? process.env.GPMPAY_API_TOKEN;
  if (token === undefined || token.trim() === '') {
    throw new GpmPayConfigError(NO_TOKEN_MESSAGE, 'missing_api_token');
  }

  const baseUrl =
    flags.baseUrl ??
    (flags.sandbox === true ? SANDBOX_BASE_URL : undefined) ??
    process.env.GPMPAY_API_URL;

  return new GpmPay({
    apiToken: token,
    ...(baseUrl === undefined ? {} : { baseUrl }),
    userAgent: 'gpmpay-cli',
  });
}

export const NO_SECRET_MESSAGE =
  'Missing webhook secret. Pass --secret, or set GPMPAY_WEBHOOK_SECRET.\n' +
  '  This is the `authorizationSecret` you configured on the webhook setting.';

/**
 * Resolve the webhook signing secret. Lives here rather than in the webhook
 * command because `webhook send` needs it without needing a client, and
 * importing it from a sibling command module would be a cycle.
 *
 * @throws {GpmPayConfigError} when neither the flag nor the env var is set.
 */
export function secretFromFlags(flags: {
  secret?: string | undefined;
}): string {
  const secret = flags.secret ?? process.env.GPMPAY_WEBHOOK_SECRET;
  if (secret === undefined || secret === '') {
    throw new GpmPayConfigError(NO_SECRET_MESSAGE, 'invalid_argument');
  }
  return secret;
}
